import { sampleStemAsWritten } from '../../Util/fileNames.ts'
import { FILE_NAME_CONTROL_FAMILIES, POSITIVE_CONTROL, STRICT_CONTROL_FAMILIES } from '../../Util/sheetItemRoles/vocabulary.ts'

/**
 * Negative controls from names. File names get the whole list, sample names only the strict NC/negative
 * families; the word list itself lives with the sheet readers, which outlive the wizard.
 */

/** Word tokens: split at separators and at lower→upper case changes, so `PCRneg` and `ExtractionBlank` split too. */
export const nameTokens = (name: string): string[] =>
  name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((token) => token.toLowerCase())

const matches = (tokens: readonly string[], families: readonly RegExp[]) =>
  tokens.some((token) => families.some((family) => family.test(token)))

/** Joined tokens catch compounds written without separators or case changes, such as `pcrneg` inside `PCRneg1`. */
const tokensWithCompounds = (name: string) => {
  const tokens = nameTokens(name)
  return [...tokens, ...nameTokens(name.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2'))]
}

export const isControlFileName = (fileName: string): boolean => {
  const tokens = tokensWithCompounds(sampleStemAsWritten(fileName))
  if (tokens.some((token) => POSITIVE_CONTROL.test(token))) {
    return false
  }
  return matches(tokens, STRICT_CONTROL_FAMILIES) || matches(tokens, FILE_NAME_CONTROL_FAMILIES)
}

export const isControlSampleName = (sampleName: string): boolean => {
  const tokens = tokensWithCompounds(sampleName)
  if (tokens.some((token) => POSITIVE_CONTROL.test(token))) {
    return false
  }
  return matches(tokens, STRICT_CONTROL_FAMILIES)
}

export type ControlDecision = 'auto' | 'none-found' | 'too-many'

export type ControlCandidate = { key: string; fileName: string; sampleName?: string }

/** Above this share of controls among at least {@link SAFEGUARD_MIN_ITEMS} items, the labels are a question, not a fact. */
const SAFEGUARD_SHARE = 0.75
const SAFEGUARD_MIN_ITEMS = 3

/**
 * The measured order-level policy: label automatically only when at least one control is found; with
 * none found, ask once; with at least 3 items of which more than 75% would be controls, ask instead.
 */
export const labelNegativeControls = (items: readonly ControlCandidate[]): { flagged: string[]; decision: ControlDecision } => {
  const flagged = items
    .filter((item) => isControlFileName(item.fileName) || (!!item.sampleName && isControlSampleName(item.sampleName)))
    .map((item) => item.key)
  if (flagged.length === 0) {
    return { flagged, decision: 'none-found' }
  }
  if (items.length >= SAFEGUARD_MIN_ITEMS && flagged.length / items.length > SAFEGUARD_SHARE) {
    return { flagged, decision: 'too-many' }
  }
  return { flagged, decision: 'auto' }
}
